import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { logImpression, logUserAction } from '@office-start/telemetry-service';
import { KnownImpressionInstrumentationEvents } from '@office-start/telemetry-types';
import { Subscription } from 'rxjs';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { ThemingService } from '../shared/services/theming.service';
import { VisualService } from '../shared/services/visual.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { LocService } from '../shared/shared-context/loc.service';
import { NewUserShelfConfigService } from './new-user-shelf-config.service';

import './new-user-shelf.component.css';

@Component({
    selector: 'ohp-new-user-shelf',
    templateUrl: './new-user-shelf.component.html'
})

export class NewUserShelfComponent implements OnDestroy, OnInit {
    @Input() public collapsed: boolean;
    public isConsumer: boolean;
    public shelfImage1: string;
    public shelfImage2: string;
    public shelfImage1Small: string;
    public shelfImage2Small: string;
    public shelfImage1Dark: string;
    public shelfImage2Dark: string;
    public shelfImage1SmallDark: string;
    public shelfImage2SmallDark: string;
    public shelfTitle: string;
    public shelfTitleConsumer: string;
    public shelfContentSubtitle: string;
    public createNew: string;
    public exploreApps: string;
    public fluentRefresh: boolean;
    public isVisualRefresh: boolean;

    private readonly darkModeSubscription: Subscription;
    private readonly visualRefreshSubscription: Subscription;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly newUserShelfConfigService: NewUserShelfConfigService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly themingService: ThemingService,
        private readonly visualService: VisualService
    ) {
        const config = this.newUserShelfConfigService.config;
        this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
        this.shelfTitle = this.locService.NewUserShelfTitle;
        this.shelfTitleConsumer = this.locService.NewConsumerExperienceTitle;
        this.shelfContentSubtitle = this.locService.NewUserShelfSubtitle;
        this.createNew = this.locService.NewUserShelfCreateNew;
        this.exploreApps = this.locService.NewUserShelfExploreApps;
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;

        if (this.capabilitiesService.capabilities.darkModeEnabled && !this.isConsumer) { // TODO: update for HWA (consumer web doesn't have darkmode but pwa does?)
            this.darkModeSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                if (typeof event !== 'undefined') {
                    if (event) {
                        this.shelfImage1 = config.newUserShelfImage1Dark;
                        this.shelfImage2 = config.newUserShelfImage2Dark;
                        this.shelfImage1Small = config.newUserShelfImage1SmallDark;
                        this.shelfImage2Small = config.newUserShelfImage2SmallDark;
                    } else {
                        this.shelfImage1 = config.newUserShelfImage1;
                        this.shelfImage2 = config.newUserShelfImage2;
                        this.shelfImage1Small = config.newUserShelfImage1Small;
                        this.shelfImage2Small = config.newUserShelfImage2Small;
                    }
                }
            });
        } else {
            this.shelfImage1 = config.newUserShelfImage1;
            this.shelfImage2 = config.newUserShelfImage2;
            this.shelfImage1Small = config.newUserShelfImage1Small;
            this.shelfImage2Small = config.newUserShelfImage2Small;
        }

        if (this.capabilitiesService.capabilities.visualBackgroundEnabled) {
            this.visualRefreshSubscription = this.visualService.visualConfigSubscription.subscribe(visualEvent => {
                if (this.isVisualRefresh !== visualEvent.visualBackgroundEnabled) {
                    this.isVisualRefresh = visualEvent.visualBackgroundEnabled;
                }
            });
        }
    }

    public ngOnInit(): void {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = this.collapsed ? 'collapsed' : 'expanded';
        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.NewUserShelfImpression, params);
        logImpression({
            eventName: KnownImpressionInstrumentationEvents.NewUserShelfImpression,
            area: 'NewUserShelf',
            distribution: this.collapsed ? 'collapsed' : 'expanded'
        });
    }

    public onClickCreateNew(): void {
        this.officeRouteService.navigateTo({ route: OfficeRoute.Create });
        this.instrumentClick('create-new');
    }

    public onClickExploreApps(): void {
        this.officeRouteService.navigateTo({ route: OfficeRoute.AppGallery });
        this.instrumentClick('explore-apps');
    }

    public ngOnDestroy() {
        this.darkModeSubscription?.unsubscribe();
        this.visualRefreshSubscription?.unsubscribe();
    }

    private instrumentClick(action: string) {
        // TODO: add other params
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = this.collapsed ? 'collapsed' : 'expanded';
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.NewUserShelfClick, action, 'new-user-shelf', null, null, params);
        logUserAction({
            eventName: KnownInstrumentationEvents.NewUserShelfClick,
            id: action,
            area: 'NewUserShelf',
            result: this.collapsed ? 'Close' : 'Expand',
            target: 'Other'
        });
    }
}
